import type { ReactElement } from "react";
// One small line icon per practice area of the PMO Self-Assessment, drawn
// inline so the printed PDF keeps it (no icon font, no image). Kenneth,
// 2026-10-28: "add some representative icons to the improvement
// opportunities". Keyed by the instrument's block id; an area the set does
// not know gets a plain marker, so a new instrument never breaks the report.
// Decoration only: no number, no state, nothing the model does not say.

const STROKE = { fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" } as const;

const ICONS: Record<string, ReactElement> = {
  // a flag: the mandate
  mandate: <><path d="M6 21V4" /><path d="M6 4h11l-2.5 3.5L17 11H6" /></>,
  // a forum table with three seats: governance and decision rights
  governance: <><rect x="4" y="10" width="16" height="5" rx="1" /><path d="M8 10V7M12 10V6M16 10V7M7 15v4M17 15v4" /></>,
  // stage gates: three chevrons
  lifecycle: <><path d="M4 6l4 6-4 6M10 6l4 6-4 6M16 6l4 6-4 6" /></>,
  // a calendar with a milestone mark
  planning: <><rect x="4" y="5" width="16" height="15" rx="1.5" /><path d="M4 10h16M8 3v4M16 3v4" /><path d="M10 15l2 2 3-4" /></>,
  // a warning triangle: risks, issues and escalation
  raid: <><path d="M12 4l9 16H3z" /><path d="M12 10v4M12 17.5v.5" /></>,
  // crop marks: scope and change control
  change: <><path d="M7 3v14h14" /><path d="M3 7h14v14" /></>,
  // coins: financial and capacity control
  finance_capacity: <><ellipse cx="12" cy="7" rx="7" ry="3" /><path d="M5 7v5c0 1.7 3.1 3 7 3s7-1.3 7-3V7" /><path d="M5 12v5c0 1.7 3.1 3 7 3s7-1.3 7-3v-5" /></>,
  // a delivery box: suppliers
  suppliers: <><path d="M4 8l8-4 8 4v8l-8 4-8-4z" /><path d="M4 8l8 4 8-4M12 12v8" /></>,
  // a rocket: readiness, rollout and transition
  readiness: <><path d="M12 3c3 2 4 6 4 9l-4 4-4-4c0-3 1-7 4-9z" /><path d="M8 12l-3 3 3 .5M16 12l3 3-3 .5M10.5 17.5L12 21l1.5-3.5" /></>,
  // a bar chart: reporting and management information
  reporting: <><path d="M4 20h16" /><rect x="6" y="11" width="3" height="7" /><rect x="11" y="6" width="3" height="12" /><rect x="16" y="9" width="3" height="9" /></>,
  // two people: roles, competencies and capacity
  roles: <><circle cx="9" cy="8" r="3" /><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" /><circle cx="17" cy="9" r="2.3" /><path d="M15.5 14.5c2.7 0 5 1.8 5 4.5" /></>,
  // a wrench: tools, data and registers
  tools_data: <><path d="M14.5 6.5a4 4 0 0 0 5 5L9 22l-3-3L16.5 8.5" /><path d="M14.5 6.5L17 4l3 3-2.5 2.5" /></>,
  // a target with a rising arrow: benefits and value realisation
  benefits: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="M12 12l6-6M15 6h3v3" /></>,
  // a cycle: continuous improvement
  pmo_performance: <><path d="M20 12a8 8 0 0 1-13.7 5.6" /><path d="M4 12a8 8 0 0 1 13.7-5.6" /><path d="M17.7 3v3.4h-3.4M6.3 21v-3.4h3.4" /></>,
};

const GENERIC = <><circle cx="12" cy="12" r="7" /><path d="M9 12h6" /></>;

export function AreaIcon({ blockId, size = 20, label }: { blockId: string; size?: number; label?: string }) {
  const glyph = ICONS[blockId] ?? GENERIC;
  return (
    <svg
      className="area-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role={label ? "img" : "presentation"}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      style={{ flex: "none", verticalAlign: "middle", color: "var(--accent, #264060)" }}
      {...STROKE}
    >
      {glyph}
    </svg>
  );
}

export const AREA_ICON_IDS = Object.keys(ICONS);
